import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Container,
  Table,
} from "reactstrap";
import { useAppDispatch } from "src/store";
import { baseUrl } from "src/request/types";
import {
  deleteTutorialVideoSaga,
  getTutorialVideoDetailsSaga,
  getTutorialVideosSaga,
} from "src/store/tutorials/saga";
import { TutorialVideoDto } from "src/store/tutorials/type";
import TutorialVideoCreateForm from "./video-create";
import TutorialVideoEditForm from "./video-edit";

const fmtDur = (s: number) =>
  Math.floor((s || 0) / 60) + ":" + String((s || 0) % 60).padStart(2, "0");

const TutorialVideosPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { playlistId = "" } = useParams<{ playlistId: string }>();

  const [videos, setVideos] = useState<TutorialVideoDto[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selected, setSelected] = useState<TutorialVideoDto & { Translates?: any[] }>();

  const fetchData = useCallback(() => {
    dispatch(
      getTutorialVideosSaga({
        id: playlistId,
        payload: {
          onSuccess: (_m: string, p: TutorialVideoDto[]) =>
            setVideos(Array.isArray(p) ? p : []),
          onError: () => setVideos([]),
        },
      })
    );
  }, [dispatch, playlistId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onEdit = (id: string) => {
    dispatch(
      getTutorialVideoDetailsSaga({
        id,
        payload: {
          onSuccess: (_m: string, payload: any, response: any) => {
            const obj = payload;
            obj.Translates = response.Translates;
            setSelected(obj);
            setShowEdit(true);
          },
          onError: () => {},
        },
      })
    );
  };

  const onDelete = (id: string) => {
    if (!window.confirm(t("Tutorials.DeleteVideoConfirm").toString())) return;
    dispatch(
      deleteTutorialVideoSaga({
        id,
        payload: {
          onSuccess: (m: string) => {
            toast.success(m, { theme: "colored" });
            fetchData();
          },
          onError: (m: string) => toast.error(m, { theme: "colored" }),
        },
      })
    );
  };

  const refresh = (m: string) => {
    toast.success(m, { theme: "colored" });
    fetchData();
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <Link to="/tutorials" className="text-muted">
              <i className="bx bx-arrow-back me-1" /> {t("Tutorials.BackToPlaylists")}
            </Link>
            <h4 className="mb-0 mt-1">{t("Tutorials.Videos")}</h4>
          </div>
          <Button color="primary" onClick={() => setShowCreate(true)}>
            <i className="bx bx-plus me-1" /> {t("Tutorials.AddVideo")}
          </Button>
        </div>

        <Card>
          <CardBody>
            <Table responsive className="align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>{t("Tutorials.Poster")}</th>
                  <th>{t("Tutorials.Title")}</th>
                  <th>{t("Tutorials.DurationSec")}</th>
                  <th>{t("Tutorials.Order")}</th>
                  <th>{t("Tutorials.Video")}</th>
                  <th>{t("Tutorials.IsActive")}</th>
                  <th className="text-end">{t("Tutorials.Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {videos.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      {t("Tutorials.NoVideos")}
                    </td>
                  </tr>
                )}
                {videos.map((v) => (
                  <tr key={v.Id}>
                    <td>
                      {v.Poster ? (
                        <img
                          src={baseUrl + v.Poster}
                          alt=""
                          style={{ width: 72, height: 44, objectFit: "cover", borderRadius: 6 }}
                        />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="fw-medium">{v.Title || "-"}</div>
                      <div className="text-muted small">{v.Description}</div>
                    </td>
                    <td>{fmtDur(v.DurationSec)}</td>
                    <td>{v.Order}</td>
                    <td>
                      {v.Video ? (
                        <a href={baseUrl + v.Video} target="_blank" rel="noreferrer">
                          <i className="bx bx-play-circle" /> {t("Tutorials.Open")}
                        </a>
                      ) : (
                        <span className="text-danger small">{t("Tutorials.NoFile")}</span>
                      )}
                    </td>
                    <td>
                      {v.IsActive ? (
                        <span className="badge bg-success">{t("Tutorials.Active")}</span>
                      ) : (
                        <span className="badge bg-secondary">{t("Tutorials.Passive")}</span>
                      )}
                    </td>
                    <td className="text-end">
                      <Button color="light" size="sm" className="me-2" onClick={() => onEdit(v.Id)}>
                        <i className="bx bx-edit" />
                      </Button>
                      <Button color="light" size="sm" onClick={() => onDelete(v.Id)}>
                        <i className="bx bx-trash text-danger" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </Container>

      <TutorialVideoCreateForm
        playlistId={playlistId}
        open={showCreate}
        onHide={() => setShowCreate(false)}
        onSuccessClick={refresh}
      />
      {selected && (
        <TutorialVideoEditForm
          data={selected}
          open={showEdit}
          onHide={() => {
            setShowEdit(false);
            setSelected(undefined);
          }}
          onSuccessClick={refresh}
        />
      )}
    </div>
  );
};

export default TutorialVideosPage;
